import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Calendar, Clock, User, Phone, CheckCircle, ArrowLeft } from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { services } from "../data/mock";
import "../styles/Booking.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BookingPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const serviceId = searchParams.get("serviceId");
  
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  
  const [formData, setFormData] = useState({
    customerName: "",
    customerPhone: "",
    consentConfirmed: false
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (serviceId) {
      const service = services.find(s => s.id === parseInt(serviceId));
      setSelectedService(service);
    }
  }, [serviceId]);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(selectedDate);
    }
  }, [selectedDate]);

  const fetchAvailableSlots = async (date) => {
    setLoadingSlots(true);
    setSelectedTime("");
    
    try {
      const response = await fetch(`${API_URL}/api/bookings/available-slots?date=${date}`);
      const data = await response.json();
      
      if (data.success) {
        setAvailableSlots(data.slots);
      }
    } catch (err) {
      console.error("Error fetching slots:", err);
      setError("Failed to load available time slots");
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!selectedService || !selectedDate || !selectedTime || !formData.consentConfirmed) {
      setError("Please complete all fields and confirm consent");
      return;
    }

    setIsSubmitting(true);

    try {
      const servicePrice = parseFloat(selectedService.price.replace("£", ""));
      
      const bookingRequest = {
        service_id: selectedService.id,
        service_title: selectedService.title,
        service_price: servicePrice,
        service_duration: selectedService.duration,
        appointment_date: selectedDate,
        appointment_time: selectedTime,
        customer_name: formData.customerName,
        customer_phone: formData.customerPhone,
        consent_confirmed: formData.consentConfirmed,
        origin_url: window.location.origin
      };

      const response = await fetch(`${API_URL}/api/bookings/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(bookingRequest)
      });

      const data = await response.json();

      if (response.ok && data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      } else {
        throw new Error(data.detail || "Failed to create booking");
      }
    } catch (err) {
      console.error("Booking error:", err);
      setError(err.message || "Failed to create booking. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  if (!selectedService) {
    return (
      <div className="booking-page">
        <Navigation />
        <div className="booking-container">
          <p>Service not found. Please select a service to book.</p>
          <button onClick={() => navigate("/")} className="btn-secondary">
            <ArrowLeft size={20} /> Back to Services
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const depositAmount = (parseFloat(selectedService.price.replace("£", "")) * 0.20).toFixed(2);

  return (
    <div className="booking-page">
      <Navigation />
      
      <div className="booking-container">
        <button onClick={() => navigate("/")} className="back-button">
          <ArrowLeft size={20} /> Back to Services
        </button>

        <div className="booking-header">
          <h1 className="heading-1">Book Your Appointment</h1>
          <p className="body-large">Complete your booking in just a few steps</p>
        </div>

        <div className="booking-content">
          <div className="booking-summary">
            <h3 className="heading-3">Booking Summary</h3>
            <div className="summary-item">
              <strong>Service:</strong> {selectedService.title}
            </div>
            <div className="summary-item">
              <strong>Duration:</strong> {selectedService.duration}
            </div>
            <div className="summary-item">
              <strong>Full Price:</strong> {selectedService.price}
            </div>
            <div className="summary-item deposit-highlight">
              <strong>Deposit (20%):</strong> £{depositAmount}
            </div>
            <p className="body-small" style={{marginTop: "20px", color: "var(--text-muted)"}}>               Remaining balance due on arrival
            </p>
          </div>

          <form onSubmit={handleSubmit} className="booking-form">
            <div className="form-section">
              <h3 className="heading-3">
                <Calendar size={24} /> Select Date
              </h3>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                min={getMinDate()}
                required
                className="form-input"
              />
            </div>

            {selectedDate && (
              <div className="form-section">
                <h3 className="heading-3">
                  <Clock size={24} /> Select Time
                </h3>
                {loadingSlots ? (
                  <p className="body-regular">Loading available slots...</p>
                ) : availableSlots.length === 0 ? (
                  <p className="body-regular" style={{color: "var(--error)"}}>                    No available slots for this date. Please choose another date.
                  </p>
                ) : (
                  <div className="time-slots">
                    {availableSlots.map((slot) => (
                      <button
                        key={slot}
                        type="button"
                        onClick={() => setSelectedTime(slot)}
                        className={`time-slot ${selectedTime === slot ? "selected" : ""}`}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="form-section">
              <h3 className="heading-3">
                <User size={24} /> Your Information
              </h3>
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  value={formData.customerName}
                  onChange={(e) => setFormData({...formData, customerName: e.target.value})}
                  required
                  className="form-input"
                  placeholder="Enter your full name"
                />
              </div>
              <div className="form-group">
                <label>
                  <Phone size={16} /> Phone Number *
                </label>
                <input
                  type="tel"
                  value={formData.customerPhone}
                  onChange={(e) => setFormData({...formData, customerPhone: e.target.value})}
                  required
                  className="form-input"
                  placeholder="+44 ..."
                />
              </div>
            </div>

            <div className="form-section">
              <h3 className="heading-3">
                <CheckCircle size={24} /> Consent
              </h3>
              <div className="consent-checkbox">
                <input
                  type="checkbox"
                  id="consent"
                  checked={formData.consentConfirmed}
                  onChange={(e) => setFormData({...formData, consentConfirmed: e.target.checked})}
                  required
                />
                <label htmlFor="consent">
                  I confirm that I have read and completed the{" "}
                  <a href="https://form.jotform.com/243465625419361" target="_blank" rel="noopener noreferrer">
                    medical history and consent form
                  </a>
                  {" "}and agree to the{" "}
                  <a href="/policies" target="_blank" rel="noopener noreferrer">
                    terms and conditions
                  </a>
                </label>
              </div>
            </div>

            {error && (
              <div className="form-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="btn-primary btn-large"
              disabled={isSubmitting || !formData.consentConfirmed}
            >
              {isSubmitting ? "Processing..." : `Pay Deposit £${depositAmount}`}
            </button>

            <p className="body-small" style={{textAlign: "center", marginTop: "20px", color: "var(--text-muted)"}}>              Secure payment powered by Stripe
            </p>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BookingPage;